import React, { useState, useRef, Fragment, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Patient, Encounter } from '../../types';
import { Search, FileText, Printer, Trash2, Download, Eye, ZoomIn, ZoomOut, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 *
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 * Content slices are taken from the source canvas one page-height at a time
 * so rows never get clipped mid-content across page breaks.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  // A4 geometry (millimetres)
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  // How many source pixels equal one printed content page (in canvas pixels)?
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  // Slice the source canvas page-by-page; render each slice into its own
  // temporary canvas, paint that into the PDF. This avoids the "shift the
  // whole image up" trick that clips rows mid-cell.
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
type ViewingTest = {
  kind: 'lab';
  encounterId: string;
  labId: string;
} | {
  kind: 'rad';
  encounterId: string;
  radId: string;
} | null;
export const PatientFiles: React.FC = () => {
  const {
    patients,
    encounters,
    deletePatient,
    currentUser
  } = useData();
  const navigate = useNavigate();
  // Amendment modal — Clinicians can reopen a completed encounter, provide a
  // required reason, and the Clinician Dashboard picks up that context on
  // arrival to log audit events on every save.
  const [amendModal, setAmendModal] = useState<{
    isOpen: boolean;
    encounterId: string;
    reason: string;
  }>({
    isOpen: false,
    encounterId: '',
    reason: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [viewingTest, setViewingTest] = useState<ViewingTest>(null);
  const [imageZoom, setImageZoom] = useState<number>(100); // percentage 10-200
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const testModalRef = useRef<HTMLDivElement>(null);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const handleDeletePatient = (id: string) => {
    if (window.confirm('WARNING: This will permanently delete the patient and ALL associated medical records. Are you sure?')) {
      deletePatient(id);
      setSelectedPatient(null);
    }
  };
  const handlePrint = () => {
    window.print();
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedPatient) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_PatientFile_${selectedPatient.bmcFileNumber}_${selectedPatient.firstName}_${selectedPatient.lastName}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF. Please try again or use the Print option.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  // ─── Test File modal helpers ───
  const closeTestModal = () => {
    setViewingTest(null);
    setImageZoom(100);
  };
  const handlePrintTestModal = () => {
    // Use a print-only class on body so only the modal prints
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${selectedPatient?.bmcFileNumber || 'patient'}_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // ─── PATIENT FILE VIEW ───
  if (selectedPatient) {
    const patientEncounters = getPatientEncounters(selectedPatient.id);
    return <div className="space-y-6" data-id="element-297">
        {/* Action Bar */}
        <div className="flex justify-between items-center print:hidden" data-id="element-298">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-299">Patient File</h1>
          <div className="flex space-x-2" data-id="element-300">
            <Button variant="outline" onClick={() => setSelectedPatient(null)} data-id="element-301">
              Back to Directory
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-302">
              <Printer className="w-4 h-4 mr-2" data-id="element-303" /> Print
            </Button>
            <Button variant="primary" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-304">
              <Download className="w-4 h-4 mr-2" data-id="element-305" />
              {isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}
            </Button>
            {currentUser?.role === 'Administrator' && <Button variant="danger" onClick={() => handleDeletePatient(selectedPatient.id)} data-id="element-306">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-307" /> Delete File
              </Button>}
          </div>
        </div>

        {/* Encounter Status Summary (screen only) */}
        <div className="print:hidden bg-white rounded-lg shadow-sm p-4 border border-gray-200" data-id="element-308">
          <h3 className="text-sm font-semibold text-gray-700 mb-2" data-id="element-309">
            Encounter History ({patientEncounters.length})
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-310">
            {patientEncounters.map((enc, idx) => <span key={enc.id} className={`px-3 py-1 rounded-full text-xs font-medium ${enc.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-311">
                Visit {patientEncounters.length - idx}:{' '}
                {new Date(enc.startedAt).toLocaleDateString()} •{' '}
                {enc.stage === 'COMPLETED' ? '✓ Completed' : enc.stage}
              </span>)}
          </div>
        </div>

        {/* All Encounters – Always Expanded — wrapped in ref for PDF capture */}
        <div ref={printableRef} data-id="element-312">
          {patientEncounters.length > 0 ? patientEncounters.map((enc, index) => <div key={enc.id} className={`bg-white border-2 border-gray-300 rounded-lg p-6 mb-6 ${index > 0 ? 'print:break-before-page pdf-page-break' : ''}`} data-id="element-313">
                {/* Clinician-only Amend action for completed visits */}
                {currentUser?.role === 'Clinician' && enc.stage === 'COMPLETED' && <div className="print:hidden flex justify-end mb-3" data-id="element-314">
                      <Button variant="outline" className="text-amber-700 border-amber-400 hover:bg-amber-50" onClick={() => setAmendModal({
              isOpen: true,
              encounterId: enc.id,
              reason: ''
            })} data-id="element-315">
                        <FileText className="w-4 h-4 mr-2" data-id="element-316" />
                        Amend File
                      </Button>
                    </div>}
                {/* ═══ FORM HEADER ═══ */}
                <div className="flex justify-between items-start mb-6" data-id="element-317">
                  <div className="flex items-center" data-id="element-318">
                    <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-319" />
                    <div data-id="element-320">
                      <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-321">
                        BMED MEDICAL CENTRE
                      </h1>
                      <p className="text-sm italic text-gray-600" data-id="element-322">
                        Caring Healing Leading
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-323">
                    <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-324">
                      Confidential
                    </p>
                    <p className="text-sm" data-id="element-325">
                      <span className="font-semibold" data-id="element-326">Date:</span>{' '}
                      {new Date(enc.startedAt).toLocaleDateString()}
                    </p>
                    <p className="text-sm" data-id="element-327">
                      <span className="font-semibold" data-id="element-328">File No:</span>{' '}
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                </div>

                {/* ═══ PERSONAL DETAILS ═══ */}
                <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-329">
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-330">
                    <div className="col-span-1 font-semibold" data-id="element-331">1.</div>
                    <div className="col-span-6" data-id="element-332">
                      <span className="font-semibold" data-id="element-333">Full Names:</span>{' '}
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </div>
                    <div className="col-span-3" data-id="element-334">
                      <span className="font-semibold" data-id="element-335">Sex:</span>{' '}
                      {selectedPatient.sex}
                    </div>
                    <div className="col-span-2" data-id="element-336">
                      <span className="font-semibold" data-id="element-337">Age:</span>{' '}
                      {selectedPatient.age}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-338">
                    <div className="col-span-1 font-semibold" data-id="element-339">2.</div>
                    <div className="col-span-11" data-id="element-340">
                      <span className="font-semibold" data-id="element-341">Marital Status:</span>{' '}
                      {selectedPatient.maritalStatus}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-342">
                    <div className="col-span-1 font-semibold" data-id="element-343">3.</div>
                    <div className="col-span-7" data-id="element-344">
                      <span className="font-semibold" data-id="element-345">Home Address:</span>{' '}
                      {selectedPatient.address}
                    </div>
                    <div className="col-span-4" data-id="element-346">
                      <span className="font-semibold" data-id="element-347">Contact:</span>{' '}
                      {selectedPatient.contact}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-348">
                    <div className="col-span-1 font-semibold" data-id="element-349">4.</div>
                    <div className="col-span-11" data-id="element-350">
                      <span className="font-semibold" data-id="element-351">Occupation:</span>{' '}
                      {selectedPatient.occupation || '_________________________'}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-352">
                    <div className="col-span-1 font-semibold" data-id="element-353">5.</div>
                    <div className="col-span-5" data-id="element-354">
                      <span className="font-semibold" data-id="element-355">Next of Kin:</span>{' '}
                      {selectedPatient.nextOfKin}
                    </div>
                    <div className="col-span-3" data-id="element-356">
                      <span className="font-semibold" data-id="element-357">Contact:</span>{' '}
                      {selectedPatient.nokContact}
                    </div>
                    <div className="col-span-3" data-id="element-358">
                      <span className="font-semibold" data-id="element-359">Relationship:</span>{' '}
                      {selectedPatient.nokRelationship}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-360">
                    <div className="col-span-1 font-semibold" data-id="element-361">6.</div>
                    <div className="col-span-11" data-id="element-362">
                      <span className="font-semibold" data-id="element-363">Allergies:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-364">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2" data-id="element-365">
                    <div className="col-span-1 font-semibold" data-id="element-366">7.</div>
                    <div className="col-span-11" data-id="element-367">
                      <span className="font-semibold" data-id="element-368">HIV Status:</span>{' '}
                      <span className="ml-4" data-id="element-369">
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-370">
                          Positive{' '}
                          {selectedPatient.hivStatus === 'Positive' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-371">
                          Negative{' '}
                          {selectedPatient.hivStatus === 'Negative' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black ${selectedPatient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-372">
                          Unknown{' '}
                          {selectedPatient.hivStatus === 'Unknown' ? '✓' : ''}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* ═══ NURSING OBSERVATIONS ═══ */}
                <div className="mb-6" data-id="element-373">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-374">
                    Nursing Observations
                  </h3>
                  <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-375">
                    <thead data-id="element-376">
                      <tr className="bg-gray-50" data-id="element-377">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-378">
                            {n}
                          </th>)}
                      </tr>
                      <tr className="bg-gray-100" data-id="element-379">
                        <th className="border border-black p-1" data-id="element-380">Temp</th>
                        <th className="border border-black p-1" data-id="element-381">Pulse</th>
                        <th className="border border-black p-1" data-id="element-382">Resp</th>
                        <th className="border border-black p-1" data-id="element-383">BP</th>
                        <th className="border border-black p-1" data-id="element-384">SpO2</th>
                        <th className="border border-black p-1" data-id="element-385">Weight (Kg)</th>
                        <th className="border border-black p-1" data-id="element-386">Height</th>
                        <th className="border border-black p-1" data-id="element-387">BMI</th>
                        <th className="border border-black p-1" data-id="element-388">Pain Score</th>
                        <th className="border border-black p-1" data-id="element-389">AVPU</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-390">
                      <tr data-id="element-391">
                        <td className="border border-black p-2 font-medium" data-id="element-392">
                          {enc.vitals?.temp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-393">
                          {enc.vitals?.pulse || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-394">
                          {enc.vitals?.resp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-395">
                          {enc.vitals?.bp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-396">
                          {enc.vitals?.sp02 || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-397">
                          {enc.vitals?.weight || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-398">
                          {enc.vitals?.height || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-399">
                          {enc.vitals?.bmi || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-400">
                          {enc.vitals?.painScore || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-401">
                          {enc.vitals?.avpu || ''}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* ═══ CLINICAL NOTES ═══ */}
                <div className="space-y-5 mb-6 text-sm" data-id="element-402">
                  <div className="border-2 border-black" data-id="element-403">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-404">
                      Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-405">
                      {enc.clinicalNote?.presentingComplaint || ''}
                    </div>
                  </div>

                  <div className="border-2 border-black" data-id="element-406">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-407">
                      History of Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-408">
                      {enc.clinicalNote?.historyOfPresentingComplaint || ''}
                    </div>
                  </div>

                  {/* PAST MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-409">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-410">
                      Past Medical History / General Co-Morbidities
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-411">
                      <tbody data-id="element-412">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-413">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-414">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-415">
                              {enc.clinicalNote?.pastMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* DRUG HISTORY */}
                  <div className="border-2 border-black" data-id="element-416">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-417">
                      Drug History
                    </h3>
                    <div className="p-3 min-h-[40px]" data-id="element-418">
                      {enc.clinicalNote?.drugHistory || ''}
                    </div>
                    <div className="p-3 border-t border-gray-300" data-id="element-419">
                      <span className="font-semibold" data-id="element-420">Any known allergy:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-421">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>

                  {/* FAMILY MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-422">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-423">
                      Family Medical History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-424">
                      <tbody data-id="element-425">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-426">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-427">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-428">
                              {enc.clinicalNote?.familyMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
                  <div className="border-2 border-black" data-id="element-429">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-430">
                      Obstetrics and Gynaecology History
                    </h3>
                    <div className="p-3 min-h-[60px] whitespace-pre-wrap" data-id="element-431">
                      {enc.clinicalNote?.obgynHistory || ''}
                    </div>
                  </div>

                  {/* SOCIAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-432">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-433">
                      Social History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-434">
                      <tbody data-id="element-435">
                        <tr className="border-b border-gray-300" data-id="element-436">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-437">
                            Alcohol
                          </td>
                          <td className="p-2" data-id="element-438">
                            {enc.clinicalNote?.socialHistory?.alcohol || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-439">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-440">
                            Smoking
                          </td>
                          <td className="p-2" data-id="element-441">
                            {enc.clinicalNote?.socialHistory?.smoking || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-442">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-443">
                            Occupation
                          </td>
                          <td className="p-2" data-id="element-444">
                            {enc.clinicalNote?.socialHistory?.occupation || ''}
                          </td>
                        </tr>
                        <tr data-id="element-445">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-446">
                            Mental Status
                          </td>
                          <td className="p-2" data-id="element-447">
                            {enc.clinicalNote?.socialHistory?.mentalStatus || ''}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* REVIEW OF SYSTEMS */}
                  <div className="border-2 border-black" data-id="element-448">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-449">
                      Review of Systems
                    </h3>
                    <div className="p-3 space-y-1" data-id="element-450">
                      <p data-id="element-451">
                        <span className="font-semibold" data-id="element-452">R/S:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.rs || ''}
                      </p>
                      <p data-id="element-453">
                        <span className="font-semibold" data-id="element-454">CVS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cvs || ''}
                      </p>
                      <p data-id="element-455">
                        <span className="font-semibold" data-id="element-456">CNS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cns || ''}
                      </p>
                      <p data-id="element-457">
                        <span className="font-semibold" data-id="element-458">GIT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.git || ''}
                      </p>
                      <p data-id="element-459">
                        <span className="font-semibold" data-id="element-460">GUT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.gut || ''}
                      </p>
                      <p data-id="element-461">
                        <span className="font-semibold" data-id="element-462">MSS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.mss || ''}
                      </p>
                      <p data-id="element-463">
                        <span className="font-semibold" data-id="element-464">IS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.is || ''}
                      </p>
                    </div>
                  </div>

                  {/* ON EXAMINATION */}
                  <div className="border-2 border-black" data-id="element-465">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-466">
                      On Examination
                    </h3>
                    <div className="p-3 space-y-2 text-sm" data-id="element-467">
                      {([{
                  key: 'gc',
                  label: 'G/C'
                }, {
                  key: 'febrile',
                  label: 'Temperature'
                }, {
                  key: 'pjc',
                  label: 'P | J | C'
                }, {
                  key: 's1s2',
                  label: 'S1  S2'
                }, {
                  key: 'rs',
                  label: 'R/S'
                }, {
                  key: 'pa',
                  label: 'P/A'
                }] as const).map(field => <div key={field.key} className="grid grid-cols-[6rem_1fr] gap-2" data-id="element-468">
                          <span className="font-bold" data-id="element-469">{field.label}:</span>
                          <span className="border-b border-gray-300 pb-0.5" data-id="element-470">
                            {enc.clinicalNote?.onExaminationDetails?.[field.key] || '\u00A0'}
                          </span>
                        </div>)}
                      {enc.clinicalNote?.onExamination && <div className="pt-2 mt-2 border-t border-gray-200" data-id="element-471">
                          <p className="text-xs font-bold uppercase text-gray-600 mb-1" data-id="element-472">
                            Additional Notes
                          </p>
                          <p className="whitespace-pre-wrap" data-id="element-473">
                            {enc.clinicalNote.onExamination}
                          </p>
                        </div>}
                    </div>
                  </div>

                  {/* SUMMARY OF SIGNIFICANT FINDINGS */}
                  <div className="border-2 border-black" data-id="element-474">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-475">
                      Summary of Significant Findings
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-476">
                      {enc.clinicalNote?.significantFindings?.filter(f => f.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-477">
                          {enc.clinicalNote.significantFindings.filter(f => f.trim()).map((finding, i) => <li key={i} data-id="element-478">{finding}</li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-479">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <p key={n} data-id="element-480">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* WORKING DIAGNOSIS */}
                  <div className="border-2 border-black" data-id="element-481">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-482">
                      Working Diagnosis
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-483">
                      {enc.clinicalNote?.workingDiagnosis?.filter(d => d.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-484">
                          {enc.clinicalNote.workingDiagnosis.filter(d => d.trim()).map((dx, i) => <li key={i} className="font-bold" data-id="element-485">
                                {dx}
                              </li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-486">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <p key={n} data-id="element-487">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* INVESTIGATIONS TO BE DONE */}
                  <div className="border-2 border-black" data-id="element-488">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-489">
                      Investigations To Be Done
                    </h3>
                    <div className="p-4" data-id="element-490">
                      <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-491">
                        Laboratory Tests
                      </h4>
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-492">
                        <tbody data-id="element-493">
                          {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-494">
                              {pair.map((test, ci) => {
                        if (!test) {
                          return <Fragment key={`empty-${ci}`} data-id="element-495">
                                      <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-496"></td>
                                      <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-497"></td>
                                    </Fragment>;
                        }
                        const isOrdered = enc.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                        return <Fragment key={test} data-id="element-498">
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-499">
                                      {test}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-500">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-501">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-502">
                        Radiology Scans
                      </h4>
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-503">
                        <tbody data-id="element-504">
                          {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-505">
                              {pair.map((scan, ci) => {
                        if (!scan) {
                          return <Fragment key={`empty-r-${ci}`} data-id="element-506">
                                      <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-507"></td>
                                      <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-508"></td>
                                    </Fragment>;
                        }
                        const isOrdered = enc.radiologyRequests.some(r => {
                          const a = r.scanType.toLowerCase();
                          const b = scan.toLowerCase();
                          return a.includes(b) || b.includes(a);
                        });
                        return <Fragment key={scan} data-id="element-509">
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-510">
                                      {scan}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-511">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-512">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <div className="border-t border-gray-300 pt-2" data-id="element-513">
                        <span className="font-semibold" data-id="element-514">
                          Other investigations:
                        </span>{' '}
                        {enc.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                      </div>

                      {/* Investigation Results — View File list */}
                      {(enc.labRequests.some(l => l.status === 'Completed') || enc.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-515">
                          <h4 className="font-bold mb-3 uppercase text-center" data-id="element-516">
                            Investigation Results
                          </h4>
                          <table className="w-full border-collapse text-sm" data-id="element-517">
                            <thead data-id="element-518">
                              <tr className="bg-gray-100" data-id="element-519">
                                <th className="border border-black p-1.5 text-left" data-id="element-520">
                                  Test / Scan
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-521">
                                  Type
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-522">
                                  Performed By
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-523">
                                  Completed
                                </th>
                                <th className="border border-black p-1.5 text-center w-32 print:hidden" data-id="element-524">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-525">
                              {enc.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(testName => !(lab.removedTestNames || []).includes(testName)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-526">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-527">
                                          {testName}
                                        </td>
                                        <td className="border border-black p-1.5" data-id="element-528">
                                          Laboratory
                                        </td>
                                        <td className="border border-black p-1.5" data-id="element-529">
                                          —
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-530">
                                          {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                        </td>
                                        <td className="border border-black p-1.5 text-center print:hidden" data-id="element-531">
                                          <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id,
                            testName
                          })} data-id="element-532">
                                            <Eye className="w-3.5 h-3.5 mr-1" data-id="element-533" />{' '}
                                            View File
                                          </Button>
                                        </td>
                                      </tr>))}
                              {enc.labRequests.filter(l => l.status === 'Completed' && l.otherTests).map(lab => <tr key={`${lab.id}-other`} data-id="element-534">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-535">
                                      {lab.otherTests}{' '}
                                      <span className="text-xs text-gray-500" data-id="element-536">
                                        (Other)
                                      </span>
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-537">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-538">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-539">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-540">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id
                          })} data-id="element-541">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-542" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                              {enc.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-543">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-544">
                                      {rad.scanType}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-545">
                                      Radiology
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-546">
                                      {rad.radiographerName || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-547">
                                      {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-548">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            encounterId: enc.id,
                            radId: rad.id
                          })} data-id="element-549">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-550" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table>
                        </div>}
                    </div>
                  </div>

                  {/* DRUG TREATMENT / PRESCRIPTION */}
                  <div className="border-2 border-black" data-id="element-551">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-552">
                      Drug Treatment / Prescription
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-553">
                      <thead data-id="element-554">
                        <tr className="bg-gray-100" data-id="element-555">
                          <th className="border border-black p-1.5 w-10" data-id="element-556">
                            No.
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-557">
                            Drug Name
                          </th>
                          <th className="border border-black p-1.5" data-id="element-558">Dose</th>
                          <th className="border border-black p-1.5" data-id="element-559">Route</th>
                          <th className="border border-black p-1.5" data-id="element-560">
                            Frequency
                          </th>
                          <th className="border border-black p-1.5" data-id="element-561">
                            No. of Days
                          </th>
                          <th className="border border-black p-1.5" data-id="element-562">
                            Signature
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-563">
                        {enc.prescriptions.length > 0 ? enc.prescriptions.map((rx, i) => <tr key={rx.id} data-id="element-564">
                                <td className="border border-black p-1.5" data-id="element-565">
                                  {i + 1}
                                </td>
                                <td className="border border-black p-1.5 text-left font-medium" data-id="element-566">
                                  {rx.drugName}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-567">
                                  {rx.dose}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-568">
                                  {rx.route}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-569">
                                  {rx.frequency}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-570">
                                  {rx.days}
                                </td>
                                <td className="border border-black p-1.5 italic text-xs" data-id="element-571">
                                  {enc.lastAttendingClinician || ''}
                                </td>
                              </tr>) : [1, 2, 3, 4, 5, 6, 7].map(i => <tr key={i} data-id="element-572">
                                <td className="border border-black p-3" data-id="element-573">{i}</td>
                                <td className="border border-black p-3" data-id="element-574"></td>
                                <td className="border border-black p-3" data-id="element-575"></td>
                                <td className="border border-black p-3" data-id="element-576"></td>
                                <td className="border border-black p-3" data-id="element-577"></td>
                                <td className="border border-black p-3" data-id="element-578"></td>
                                <td className="border border-black p-3" data-id="element-579"></td>
                              </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* STAT DOSES ADMINISTERED BY NURSING */}
                  {enc.statDoses && enc.statDoses.length > 0 && <div className="border-2 border-black" data-id="element-580">
                      <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-581">
                        Stat Doses Administered (Nursing)
                      </h3>
                      <table className="w-full border-collapse text-sm" data-id="element-582">
                        <thead data-id="element-583">
                          <tr className="bg-gray-100" data-id="element-584">
                            <th className="border border-black p-1.5 w-10" data-id="element-585">
                              No.
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-586">
                              Drug Name
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-587">
                              Dose
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-588">
                              Route
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-589">
                              Time Given
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-590">
                              Nurse
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-591">
                          {enc.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-592">
                              <td className="border border-black p-1.5 text-center" data-id="element-593">
                                {idx + 1}
                              </td>
                              <td className="border border-black p-1.5 font-medium" data-id="element-594">
                                {dose.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-595">
                                {dose.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-596">
                                {dose.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-597">
                                {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 italic text-xs" data-id="element-598">
                                {dose.nurseName || ''}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}

                  {/* FOLLOW UP / REVIEW DATE */}
                  <div className="border-2 border-black" data-id="element-599">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-600">
                      Next Follow Up / Review Date (If Any)
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-601">
                      <thead data-id="element-602">
                        <tr className="bg-gray-100" data-id="element-603">
                          <th className="border border-black p-1 w-10" data-id="element-604">No.</th>
                          <th className="border border-black p-1" data-id="element-605">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-606">No.</th>
                          <th className="border border-black p-1" data-id="element-607">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-608">No.</th>
                          <th className="border border-black p-1" data-id="element-609">Date</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-610">
                        {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-611">
                            <td className="border border-black p-1 font-medium" data-id="element-612">
                              {row + 1}
                            </td>
                            <td className="border border-black p-2" data-id="element-613">
                              {row === 0 && enc.reviewDate ? new Date(enc.reviewDate).toLocaleDateString() : ''}
                            </td>
                            <td className="border border-black p-1 font-medium" data-id="element-614">
                              {row + 6}
                            </td>
                            <td className="border border-black p-2" data-id="element-615"></td>
                            <td className="border border-black p-1 font-medium" data-id="element-616">
                              {row + 11}
                            </td>
                            <td className="border border-black p-2" data-id="element-617"></td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* NAME OF MEDICAL PRACTITIONER */}
                  <div className="border-2 border-black p-4 mt-6" data-id="element-618">
                    <p className="font-bold text-sm" data-id="element-619">
                      Name of the Medical Practitioner:{' '}
                      <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-620">
                        {enc.lastAttendingClinician || ''}
                      </span>
                    </p>
                  </div>
                </div>
              </div>) : <Card data-id="element-621">
              <p className="text-gray-500 text-center py-8" data-id="element-622">
                No encounters found for this patient.
              </p>
            </Card>}
        </div>

        {/* Amendment Reason Modal */}
        {amendModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-623">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-624">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-625">
                Amend Completed Visit
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-626">
                Please provide a reason for amending this completed file. Your
                name, the timestamp, and this reason will be recorded in the
                audit trail.
              </p>
              <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none" rows={4} value={amendModal.reason} onChange={e => setAmendModal({
            ...amendModal,
            reason: e.target.value
          })} placeholder="e.g., Correction of working diagnosis after MDT discussion" data-id="element-627" />
              <div className="flex justify-end space-x-3 mt-4" data-id="element-628">
                <Button variant="outline" onClick={() => setAmendModal({
              isOpen: false,
              encounterId: '',
              reason: ''
            })} data-id="element-629">
                  Cancel
                </Button>
                <Button variant="primary" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={!amendModal.reason.trim()} onClick={() => {
              navigate('/dashboard', {
                state: {
                  amendEncounterId: amendModal.encounterId,
                  amendReason: amendModal.reason.trim()
                }
              });
            }} data-id="element-630">
                  Open in Consultation
                </Button>
              </div>
            </div>
          </div>}

        {viewingTest && (() => {
        const enc = encounters.find(e => e.id === viewingTest.encounterId);
        if (!enc) return null;
        const lab = viewingTest.kind === 'lab' ? enc.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? enc.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-631">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-632">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-633">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-634">
                      {lab ? 'Laboratory Result' : 'Radiology Result'}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-635">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-636">
                        <Printer className="w-4 h-4 mr-1" data-id="element-637" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-638">
                        <Download className="w-4 h-4 mr-1" data-id="element-639" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-640">
                        <X className="w-5 h-5 text-gray-500" data-id="element-641" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-642">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-643">
                      <div className="flex items-center" data-id="element-644">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-645" />
                        <div data-id="element-646">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-647">
                            BMED MEDICAL CENTRE
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-648">
                            Caring Healing Leading
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-649">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-650">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-651">
                          <span className="font-semibold" data-id="element-652">Patient:</span>{' '}
                          {selectedPatient.firstName} {selectedPatient.lastName}
                        </p>
                        <p data-id="element-653">
                          <span className="font-semibold" data-id="element-654">File No:</span>{' '}
                          {selectedPatient.bmcFileNumber}
                        </p>
                        <p data-id="element-655">
                          <span className="font-semibold" data-id="element-656">Age/Sex:</span>{' '}
                          {selectedPatient.age} / {selectedPatient.sex}
                        </p>
                        <p data-id="element-657">
                          <span className="font-semibold" data-id="element-658">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-659">
                            <span className="font-semibold" data-id="element-660">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && (() => {
                const focusedTest = viewingTest?.kind === 'lab' ? viewingTest.testName : undefined;
                const showFBC = focusedTest && isFBCTest(focusedTest);
                const showUA = focusedTest && isUrinalysisTest(focusedTest);
                const showT = focusedTest && isTestosteroneTest(focusedTest);
                const showHP = focusedTest && isHormonalProfileTest(focusedTest);
                const showAMH = focusedTest && isAMHTest(focusedTest);
                const showHCG = focusedTest && isHCGTest(focusedTest);
                return <div data-id="element-661">
                            <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-662">
                              {focusedTest || 'Tests Performed'}
                            </h2>
                            {showFBC ? <div className="space-y-4" data-id="element-663">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-664">
                                  <h3 className="font-bold uppercase" data-id="element-665">
                                    Full Blood Count (FBC) Report
                                  </h3>
                                </div>
                                {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-666">
                                    <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-667">
                                      {cat.category}
                                    </div>
                                    <table className="w-full border-collapse text-sm" data-id="element-668">
                                      <thead data-id="element-669">
                                        <tr className="bg-gray-50" data-id="element-670">
                                          <th className="border border-black p-1.5 text-left" data-id="element-671">
                                            Test
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-672">
                                            Result
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-673">
                                            Reference Range
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-24" data-id="element-674">
                                            Unit
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody data-id="element-675">
                                        {cat.tests.map(t => <tr key={t.name} data-id="element-676">
                                            <td className="border border-black p-1.5" data-id="element-677">
                                              {t.name}
                                            </td>
                                            <td className="border border-black p-1.5 font-bold" data-id="element-678">
                                              {lab.results && lab.results[t.name] || '—'}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-679">
                                              {t.range}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-680">
                                              {t.unit}
                                            </td>
                                          </tr>)}
                                      </tbody>
                                    </table>
                                  </div>)}
                              </div> : showUA ? <div data-id="element-681">
                                <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-682">
                                  <h3 className="font-bold uppercase" data-id="element-683">
                                    Urinalysis Report
                                  </h3>
                                </div>
                                <div className="max-w-md mx-auto space-y-2" data-id="element-684">
                                  {urinalysisTests.map(t => {
                        const key = `UA_${t.name}`;
                        const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                        return <div key={t.name} className="flex items-center text-sm" data-id="element-685">
                                        <span className="w-16 font-bold" data-id="element-686">
                                          {t.name}
                                        </span>
                                        <span className="mx-3 text-gray-400" data-id="element-687">
                                          -
                                        </span>
                                        <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-688">
                                          {value || '\u00A0'}
                                        </span>
                                      </div>;
                      })}
                                </div>
                                <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-689">
                                  <div data-id="element-690">
                                    <p className="font-semibold mb-2" data-id="element-691">
                                      Dr.'s Name:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-692">
                                      {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                    </p>
                                  </div>
                                  <div data-id="element-693">
                                    <p className="font-semibold mb-2" data-id="element-694">
                                      Signature:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-695">
                                      {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                    </p>
                                  </div>
                                </div>
                              </div> : showT ? <div className="space-y-2" data-id="element-696">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-697">
                                  <h3 className="font-bold uppercase" data-id="element-698">
                                    Testosterone Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-699">
                                  <thead data-id="element-700">
                                    <tr className="bg-gray-100" data-id="element-701">
                                      <th className="border border-black p-1.5 text-left" data-id="element-702">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-703">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-704">
                                        Reference (Male)
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-705">
                                        Reference (Female)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-706">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-707">
                                    {testosteroneTests.map(t => <tr key={t.name} data-id="element-708">
                                        <td className="border border-black p-1.5" data-id="element-709">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-710">
                                          {lab.results && lab.results[`TESTO_${t.name}`] || lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-711">
                                          {t.rangeMale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-712">
                                          {t.rangeFemale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-713">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div> : showHP ? <div data-id="element-714">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-715">
                                  <h3 className="font-bold uppercase" data-id="element-716">
                                    Hormonal Profile Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-717">
                                  <thead data-id="element-718">
                                    <tr className="bg-gray-100" data-id="element-719">
                                      <th className="border border-black p-1.5 text-left" data-id="element-720">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-721">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-722">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-723">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-724">
                                    {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-725">
                                        <tr className="align-top" data-id="element-726">
                                          <td className="border border-black p-1.5 font-medium" data-id="element-727">
                                            {h.name}
                                          </td>
                                          <td className="border border-black p-1.5 font-bold" data-id="element-728">
                                            {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                          </td>
                                          <td className="border border-black p-1.5 text-xs" data-id="element-729">
                                            {h.ranges.map(r => <div key={r.label} data-id="element-730">
                                                <span className="font-medium" data-id="element-731">
                                                  {r.label}:
                                                </span>{' '}
                                                {r.value}
                                              </div>)}
                                          </td>
                                          <td className="border border-black p-1.5 text-xs" data-id="element-732">
                                            {h.unit}
                                          </td>
                                        </tr>
                                        <tr data-id="element-733">
                                          <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-734">
                                            Method: {h.method}
                                          </td>
                                        </tr>
                                      </Fragment>)}
                                  </tbody>
                                </table>
                              </div> : showAMH ? <div data-id="element-735">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-736">
                                  <h3 className="font-bold uppercase" data-id="element-737">
                                    Anti Mullerian Hormone (AMH) Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-738">
                                  <thead data-id="element-739">
                                    <tr className="bg-gray-100" data-id="element-740">
                                      <th className="border border-black p-1.5 text-left" data-id="element-741">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-742">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-743">
                                        Reference Range (by age, ng/mL)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-744">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-745">
                                    <tr className="align-top" data-id="element-746">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-747">
                                        {amhStructure.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-748">
                                        {lab.results && (lab.results[amhStructure.storageKey] || focusedTest && lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['ANTI-MÜLLERIAN HORMONE'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-749">
                                        {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-750">
                                            <span className="font-medium" data-id="element-751">
                                              {a.ageBand}:
                                            </span>{' '}
                                            {a.range}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-752">
                                        {amhStructure.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-753">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-754">
                                        Method: {amhStructure.method}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                                <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-755">
                                  <p className="font-bold uppercase mb-1" data-id="element-756">
                                    Interpretation
                                  </p>
                                  <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-757">
                                    {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-758">{line}</li>)}
                                  </ul>
                                </div>
                              </div> : showHCG ? <div data-id="element-759">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-760">
                                  <h3 className="font-bold uppercase" data-id="element-761">
                                    Beta HCG (Quantitative) Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-762">
                                  <thead data-id="element-763">
                                    <tr className="bg-gray-100" data-id="element-764">
                                      <th className="border border-black p-1.5 text-left" data-id="element-765">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-766">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-767">
                                        Reference Range (mIU/mL)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-768">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-769">
                                    {(() => {
                          const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || focusedTest && lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                          const abnormal = isHCGAbnormal(hcgValue);
                          return <>
                                          <tr className="align-top" data-id="element-770">
                                            <td className="border border-black p-1.5 font-medium" data-id="element-771">
                                              {hcgStructure.name}
                                            </td>
                                            <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-772">
                                              {hcgValue || '—'}
                                              {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-773">
                                                  Above normal threshold (&gt; 6
                                                  mIU/mL)
                                                </div>}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-774">
                                              {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-775">
                                                  <span className="font-medium" data-id="element-776">
                                                    {r.label}:
                                                  </span>{' '}
                                                  {r.range}
                                                </div>)}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-777">
                                              {hcgStructure.unit}
                                            </td>
                                          </tr>
                                          <tr data-id="element-778">
                                            <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-779">
                                              Method: {hcgStructure.method}
                                            </td>
                                          </tr>
                                        </>;
                        })()}
                                  </tbody>
                                </table>
                              </div> : <>
                                <p className="mb-4 text-sm" data-id="element-780">
                                  <span className="font-semibold" data-id="element-781">
                                    Tests Ordered:
                                  </span>{' '}
                                  {lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}
                                  {lab.otherTests ? `, ${lab.otherTests}` : ''}
                                </p>
                                {lab.results && Object.keys(lab.results).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-782">
                                    <thead data-id="element-783">
                                      <tr className="bg-gray-100" data-id="element-784">
                                        <th className="border border-black p-2 text-left" data-id="element-785">
                                          Parameter
                                        </th>
                                        <th className="border border-black p-2 text-left" data-id="element-786">
                                          Result
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody data-id="element-787">
                                      {Object.entries(lab.results).map(([k, v]) => <tr key={k} data-id="element-788">
                                            <td className="border border-black p-2 font-medium" data-id="element-789">
                                              {k}
                                            </td>
                                            <td className="border border-black p-2" data-id="element-790">
                                              {v}
                                            </td>
                                          </tr>)}
                                    </tbody>
                                  </table> : <p className="text-gray-500 italic" data-id="element-791">
                                    No structured results recorded for this
                                    test.
                                  </p>}

                                {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-792">
                                    <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-793">
                                      Comments / Observations / Interpretation
                                    </h3>
                                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-794">
                                      {lab.labComments}
                                    </pre>
                                  </div>}
                              </>}
                          </div>;
              })()}

                    {rad && <div data-id="element-795">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-796">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-797">
                            <span className="font-semibold" data-id="element-798">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-799">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-800">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-801">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-802">
                              <h3 className="font-bold" data-id="element-803">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-804">
                                <button onClick={() => setImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-805">
                                  <ZoomOut className="w-4 h-4" data-id="element-806" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={imageZoom} onChange={e => setImageZoom(Number(e.target.value))} className="w-32" data-id="element-807" />
                                <button onClick={() => setImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-808">
                                  <ZoomIn className="w-4 h-4" data-id="element-809" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-810">
                                  {imageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-811">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-812">
                                  <div style={{
                        transform: `scale(${imageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-813">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-814" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  // ─── PATIENT DIRECTORY ───
  return <div className="space-y-6" data-id="element-815">
      <div className="flex justify-between items-center" data-id="element-816">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-817">Patient Directory</h1>
      </div>

      <Card data-id="element-818">
        <div className="relative" data-id="element-819">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-820">
            <Search className="h-5 w-5 text-gray-400" data-id="element-821" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-822" />
        </div>
      </Card>

      <Card noPadding data-id="element-823">
        <div className="overflow-x-auto" data-id="element-824">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-825">
            <thead className="bg-gray-50" data-id="element-826">
              <tr data-id="element-827">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-828">
                  BMC Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-829">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-830">
                  Age/Sex
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-831">
                  Registered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-832">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-833">
              {filteredPatients.length > 0 ? filteredPatients.map(patient => <tr key={patient.id} className="hover:bg-gray-50" data-id="element-834">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-835">
                      {patient.bmcFileNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-836">
                      {patient.firstName} {patient.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-837">
                      {patient.age} / {patient.sex}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-838">
                      {new Date(patient.registeredAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-839">
                      <Button variant="outline" size="sm" onClick={() => setSelectedPatient(patient)} data-id="element-840">
                        <FileText className="w-4 h-4 mr-2" data-id="element-841" /> View File
                      </Button>
                    </td>
                  </tr>) : <tr data-id="element-842">
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-843">
                    No patients found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};